import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import dayjs from 'dayjs';
import ApiInstance from 'src/helpers/apiInstance';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import notifications from 'src/redux/notifications/actions';
import { t } from 'src/i18n';
import { isSuccess } from 'src/containers/admin/pricing/rateEngine/rateEngineShared/apiStatus';
import { triggerDownload } from 'src/containers/admin/pricing/rateCards/download';
import actions from './actions';
import actionTypes from './actionTypes';
import { headersCacheKey } from './selectors';
import type { IExportPayload, ILoadHeadersPayload, ILoadLinesPayload, IRateLinesFilterQuery } from './types';

const LINES_PATH = 'agency_contracts/:id/amendments/:amendment_id/rate_lines/:side';
const HEADERS_PATH = `${LINES_PATH}/headers`;
const EXPORT_PATH = `${LINES_PATH}/export`;

const HTTP_UNPROCESSABLE = 422;
const HTTP_CLIENT_ERROR_MIN = 400;
const HTTP_CLIENT_ERROR_MAX = 499;
// The server's refusal when the amendment resolves to no active card, or to one with no lines on this
// side for this agency or for all agencies.
const SIDE_UNAVAILABLE_CODE = 'side_unavailable';

const linesApi = new ApiInstance(LINES_PATH);
const headersApi = new ApiInstance(HEADERS_PATH);
const exportApi = new ApiInstance(EXPORT_PATH);

// The grid's query as the server reads it — the same for a page and for the export of the whole set.
const toSearch = ({ filter, column, order, filters }: IRateLinesFilterQuery) => ({
  ...(filter ? { filter } : {}),
  ...(column && order ? { column, order } : {}),
  // An echoed empty selection would be sent back as an active filter matching nothing.
  ...Object.fromEntries(Object.entries(filters).filter(([, values]) => values.length)),
});

const pathParamsFor = ({ contractId, amendmentId, side }: ILoadHeadersPayload) => ({
  id: contractId,
  amendment_id: amendmentId,
  side,
});

export function* loadHeaders({ payload }: { type: string; payload: ILoadHeadersPayload }): Generator<any, void, any> {
  try {
    const result = yield call(sendRequest, headersApi.get, { pathParams: pathParamsFor(payload) });

    if (isSuccess(result) && Array.isArray(result.data?.headers)) {
      // An empty array is a real answer — no engine version resolves — not a failure.
      yield put(
        actions.loadHeadersSuccess(
          headersCacheKey(payload.amendmentId, payload.side),
          result.data.headers,
          result.data.dimensions ?? [],
        ),
      );

      return;
    }

    logger.error(new Error(`[agencyContractsV2] ${HEADERS_PATH} answered ${result?.status ?? 'network error'}`));

    yield put(actions.loadHeadersError());
  } catch (error) {
    logger.error(error);

    yield put(actions.loadHeadersError());
  }
}

export function* loadLines({ payload }: { type: string; payload: ILoadLinesPayload }): Generator<any, void, any> {
  try {
    const { page, pageSize } = payload.query;
    const result = yield call(sendRequest, linesApi.get, {
      pathParams: pathParamsFor(payload),
      search: { page, pageSize, ...toSearch(payload.query) },
    });

    // `filteredCount` drives the load-more sentinel; a page without it would quietly end the list.
    if (isSuccess(result) && Array.isArray(result.data?.records) && typeof result.data.filteredCount === 'number') {
      const { records, filteredCount, sortableColumns = [], filters: gridFilters = [] } = result.data;

      yield put(
        actions.loadLinesSuccess({
          page,
          rows: records,
          filteredCount,
          sortableColumns,
          filters: gridFilters,
        }),
      );

      return;
    }

    logger.error(new Error(`[agencyContractsV2] ${LINES_PATH} answered ${result?.status ?? 'network error'}`));

    yield put(actions.loadLinesError(page));
  } catch (error) {
    logger.error(error);

    yield put(actions.loadLinesError(payload.query.page));
  }
}

// `Content-Disposition` is not exposed cross-origin, so the server's name is usually unavailable. It would be
// `contract_rates_<side>_<resolution date>.csv`; the resolution date is not known here, so today stands in.
const exportFileName = (side: string): string => `contract_rates_${side}_${dayjs().format('YYYY-MM-DD')}.csv`;

const isClientRefusal = (status: unknown): boolean =>
  typeof status === 'number' && status >= HTTP_CLIENT_ERROR_MIN && status <= HTTP_CLIENT_ERROR_MAX;

// Downloads the filtered set of one side as CSV. A refusal carrying the server's sentence is shown in those
// words — "try again" is wrong advice for a 403; the generic text is kept for a 5xx or a network failure.
export function* exportLines({ payload }: { type: string; payload: IExportPayload }): Generator<any, void, any> {
  try {
    // No errorType: `apiRequest` resolves on every status, so `sendRequest`'s catch never runs here —
    // the status check below is the whole error path.
    const result = yield call(sendRequest, exportApi.get, {
      pathParams: pathParamsFor(payload),
      search: toSearch(payload.query),
    });

    if (isSuccess(result) && result.data?.fileBlobUrl) {
      triggerDownload(result.data.fileBlobUrl, result.data.fileName || exportFileName(payload.side));

      return;
    }

    const message: string | undefined = isClientRefusal(result?.status) ? result.messages?.[0] : undefined;
    // `side_unavailable` is a state of the data, not a fault, so it is the one refusal not logged.
    const expected = result?.status === HTTP_UNPROCESSABLE && result.data?.code === SIDE_UNAVAILABLE_CODE;

    if (!expected) {
      logger.error(new Error(`[agencyContractsV2] ${EXPORT_PATH} answered ${result?.status ?? 'network error'}`));
    }

    yield put(notifications.requestError({ error: message || t('dash.agencies.contractsV2.rateLines.export_failed') }));
  } catch (error) {
    logger.error(error);

    yield put(notifications.requestError({ error: t('dash.agencies.contractsV2.rateLines.export_failed') }));
  } finally {
    yield put(actions.exportSettled());
  }
}

// `takeLatest`: a superseded response landing late would put one side's rows under the other's columns.
// `takeEvery` for the export: the button is disabled while one is out, and a file is not worth cancelling.
export default function* rootSaga() {
  yield all([
    takeLatest(actionTypes.LOAD_HEADERS_REQUEST, loadHeaders),
    takeLatest(actionTypes.LOAD_LINES_REQUEST, loadLines),
    takeEvery(actionTypes.EXPORT_REQUEST, exportLines),
  ]);
}
